/**
 * The editable wall-length table.
 *
 * One row per WALL (openings are excluded — they carry no number). Each row
 * shows the wall number and an input for its length. Typing a length marks the
 * wall measured and triggers a re-solve; clearing it hands the wall back to the
 * closure solver as a free wall.
 *
 * Pure-ish: it renders into a container and reports edits/selection via
 * callbacks. It owns no geometry — the model does.
 */

/**
 * @param {HTMLElement} tbody the <tbody> to render rows into
 * @param {object} handlers
 * @param {(id:string, value:string)=>void} handlers.onEditLength
 * @param {(id:string, cvAngle:number)=>void} handlers.onEditAngle
 * @param {(id:string, value:string)=>void} handlers.onEditThickness
 * @param {(id:string)=>void} handlers.onSelectRow
 * @param {(control:HTMLElement, target:object)=>void} handlers.onBindCaptureTarget
 */

// The eight Cabinet Vision angles, in a natural reading order for the picker.
// Kept in sync with geometry's CV_ANGLES (the DB enforces the same set).
const ANGLE_OPTIONS = [-180, -135, -90, -45, 0, 45, 90, 135];

export function createTable(tbody, handlers = {}) {
  /**
   * @param {object[]} walls the full chain (openings included; filtered here)
   * @param {string|null} selectedId
   * @param {(wall:object)=>string|null} displayLength
   * @param {(wall:object)=>string} displayAngle
   */
  function render(walls, selectedId, displayLength, displayAngle, displayThickness, thickPlaceholder, autoLength) {
    tbody.replaceChildren();

    // Show every segment IN CHAIN ORDER: walls numbered W1, W2, …; a door
    // (opening) shown as "Door" with its width editable and no number.
    if (walls.length === 0) {
      const row = document.createElement('tr');
      const cell = document.createElement('td');
      cell.colSpan = 6;
      cell.className = 'wall-table__empty';
      cell.textContent = 'No walls yet — draw a room and press Cleanup.';
      row.append(cell);
      tbody.append(row);
      return;
    }

    for (const segment of walls) {
      tbody.append(buildRow(segment, selectedId, displayLength, displayAngle, displayThickness, thickPlaceholder, autoLength));
    }
  }

  function buildRow(segment, selectedId, displayLength, displayAngle, displayThickness, thickPlaceholder, autoLength) {
    const isOpening = (segment.segmentKind ?? 'wall') === 'opening';
    const isPartition = isOpening && handlers.isPartition?.(segment.id);
    const isDoor = isOpening && !isPartition;
    const row = document.createElement('tr');
    row.className = 'wall-table__row';
    if (isDoor) row.classList.add('wall-table__row--door');
    if (segment.id === selectedId) row.classList.add('wall-table__row--selected');
    row.dataset.wallId = segment.id;

    // Openings show "Door"; a partition footprint shows "Part" (its length is
    // the partition thickness); real walls show their number.
    const label = isPartition ? 'Part' : isDoor ? 'Door' : `W${segment.wallNumber}`;
    const numberCell = document.createElement('td');
    numberCell.className = isOpening ? 'wall-table__number wall-table__number--door' : 'wall-table__number';
    const numberLabel = document.createElement('span');
    numberLabel.className = 'wall-table__number-label';
    numberLabel.textContent = label;
    numberCell.append(numberLabel);
    numberCell.addEventListener('click', () => handlers.onSelectRow?.(segment.id));

    // Keep the snap lock with the wall identifier instead of consuming the
    // already-narrow measurement column. At tablet display scales the previous
    // input + lock grid reduced the length field to only a few visible pixels.
    if (!isOpening) {
      const lock = document.createElement('button');
      lock.type = 'button';
      lock.className = 'wall-table__snap-lock';
      const refreshLock = () => {
        const locked = segment.snapLengthLocked === true;
        lock.classList.toggle('is-active', locked);
        lock.setAttribute('aria-pressed', String(locked));
        lock.setAttribute('aria-label', `${locked ? 'Unlock' : 'Lock'} wall ${segment.wallNumber} length for snap close`);
        lock.title = locked ? 'Locked: snap close will not adjust this wall' : 'Unlocked: snap close may adjust this wall';
        lock.textContent = locked ? '🔒' : '🔓';
      };
      refreshLock();
      lock.addEventListener('click', (event) => {
        event.stopPropagation();
        const next = segment.snapLengthLocked !== true;
        if (handlers.onToggleSnapLock?.(segment.id, next) !== false) {
          segment.snapLengthLocked = next;
          refreshLock();
        }
      });
      numberCell.append(lock);
    }

    const lengthCell = document.createElement('td');
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'wall-table__input';
    input.value = displayLength(segment) ?? '';
    input.placeholder = autoPlaceholder(segment, autoLength);
    // Amber placeholder marks an auto length so its meaning survives even when a
    // narrow column clips the word "auto" off the end.
    input.classList.toggle('wall-table__input--auto', isAutoLength(segment));
    input.setAttribute('aria-label', isPartition ? 'Partition thickness' : isDoor ? 'Door width' : `Length of wall ${segment.wallNumber}`);
    input.setAttribute('inputmode', 'text');
    input.dataset.pad = 'length'; // wall length → in-app numeric pad on touch
    const lengthTarget = isPartition
      ? { kind: 'wall_field', entityId: segment.id, property: 'partitionDepthUnits', label: 'Partition depth' }
      : isDoor
        ? { kind: 'wall_field', entityId: segment.id, property: 'doorWidthUnits', label: 'Door width' }
        : { kind: 'wall_length', entityId: segment.id, property: 'lengthUnits', label: `W${segment.wallNumber} length` };
    handlers.onBindCaptureTarget?.(input, lengthTarget);

    input.addEventListener('focus', () => handlers.onSelectRow?.(segment.id));
    input.addEventListener('click', () => {
      if (!isOpening || isPartition) handlers.onArmLength?.(segment.id);
    });
    const commit = () => {
      const ok = handlers.onEditLength?.(segment.id, input.value);
      input.classList.toggle('wall-table__input--invalid', ok === false);
    };
    input.addEventListener('change', commit);
    input.addEventListener('keydown', (evt) => {
      if (evt.key === 'Enter') {
        commit();
        input.blur();
      }
    });
    const lengthEditor = document.createElement('div');
    lengthEditor.className = 'wall-table__length-editor';
    const inherited = document.createElement('span');
    inherited.className = 'wall-table__measurement-state';
    inherited.textContent = 'Inherited';
    inherited.hidden = segment.measurementState !== 'inherited';
    inherited.title = 'Copied measurement — re-measure this value in Field Capture';
    lengthEditor.append(input, inherited);
    lengthCell.append(lengthEditor);

    // Angle is EDITABLE: cleanup snaps to the nearest 45° and sometimes snaps
    // wrong, so the designer can pick the correct Cabinet Vision angle from the
    // eight legal values. Changing it re-solves the chain (like editing length).
    const angleCell = document.createElement('td');
    angleCell.className = 'wall-table__angle';
    const angleSelect = document.createElement('select');
    angleSelect.className = 'wall-table__angle-select';
    angleSelect.setAttribute('aria-label', isOpening ? 'Segment angle' : `Angle of wall ${segment.wallNumber}`);
    for (const a of ANGLE_OPTIONS) {
      const opt = document.createElement('option');
      opt.value = String(a);
      opt.textContent = `${a}°`;
      angleSelect.append(opt);
    }
    angleSelect.value = String(segment.angleDeg ?? 0);
    angleSelect.addEventListener('focus', () => handlers.onSelectRow?.(segment.id));
    angleSelect.addEventListener('change', () => {
      const ok = handlers.onEditAngle?.(segment.id, Number(angleSelect.value));
      angleSelect.classList.toggle('wall-table__angle-select--invalid', ok === false);
    });
    angleCell.append(angleSelect);

    // "Thick" — real walls: a per-wall thickness override (blank = shop
    // default). A partition: its actual thickness (editable). Doors: nothing.
    const thickCell = document.createElement('td');
    thickCell.className = 'wall-table__thick';
    if (!isOpening || isPartition) {
      const thick = document.createElement('input');
      thick.type = 'text';
      thick.className = 'wall-table__thick-input';
      thick.value = displayThickness?.(segment) ?? '';
      thick.placeholder = thickPlaceholder ?? '';
      thick.setAttribute('aria-label', `Thickness of wall ${segment.wallNumber} (blank = default)`);
      thick.setAttribute('inputmode', 'text');
      thick.dataset.pad = 'length';
      handlers.onBindCaptureTarget?.(thick, isPartition
        ? { kind: 'wall_field', entityId: segment.id, property: 'partitionThicknessUnits', label: 'Partition thickness' }
        : { kind: 'wall_field', entityId: segment.id, property: 'wallThicknessUnits', label: `W${segment.wallNumber} thickness` });
      thick.addEventListener('focus', () => handlers.onSelectRow?.(segment.id));
      const commitThick = () => {
        const ok = handlers.onEditThickness?.(segment.id, thick.value);
        thick.classList.toggle('wall-table__input--invalid', ok === false);
      };
      thick.addEventListener('change', commitThick);
      thick.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') { commitThick(); thick.blur(); }
      });
      thickCell.append(thick);
    }

    // "Elev" — include this wall (or a partition's two faces) in the front
    // elevation. Doors get no checkbox — they aren't elevated as a panel.
    const elevCell = document.createElement('td');
    elevCell.className = 'wall-table__elev';
    if (!isOpening || isPartition) {
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.className = 'wall-table__elev-box';
      box.checked = isPartition ? handlers.partitionElevation?.(segment.id) !== false : segment.includeInElevation !== false;
      box.setAttribute('aria-label', isPartition ? 'Include this partition in the elevation' : `Include wall ${segment.wallNumber} in elevation`);
      box.addEventListener('change', () => handlers.onToggleElevation?.(segment.id, box.checked));
      elevCell.append(box);
    }

    // Delete. Cleanup can only guess at a shaky stroke, and a wrong guess used
    // to mean redrawing the whole space (owner, 2026-08-04). Every row can be
    // removed; app.js routes each kind to the operation that knows how to heal
    // the chain behind it.
    const deleteCell = document.createElement('td');
    deleteCell.className = 'wall-table__delete';
    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'wall-table__delete-btn';
    del.textContent = '×';
    del.title = isPartition ? 'Delete this partition' : isDoor ? 'Delete this door' : `Delete wall ${segment.wallNumber}`;
    del.setAttribute('aria-label', del.title);
    del.addEventListener('click', () => handlers.onDeleteSegment?.(segment.id));
    deleteCell.append(del);

    row.append(numberCell, lengthCell, angleCell, thickCell, elevCell, deleteCell);
    return row;
  }

  /**
   * Updates values, placeholders, and selection highlight on the EXISTING rows,
   * without rebuilding them. This is what makes editing possible: a full
   * rebuild on selection or on each edit would destroy the very input the user
   * is typing in (focus fires selection, selection used to re-render — the
   * input got replaced mid-keystroke and text went nowhere).
   *
   * The currently-focused input is never overwritten, so live typing is safe.
   * Falls back to a full render() when the set of rows no longer matches the
   * walls (i.e. the structure changed and rows must be created/removed).
   */
  function sync(walls, selectedId, displayLength, displayAngle, displayThickness, thickPlaceholder, autoLength) {
    const rows = tbody.querySelectorAll('.wall-table__row');
    if (rows.length !== walls.length) {
      render(walls, selectedId, displayLength, displayAngle, displayThickness, thickPlaceholder, autoLength);
      return;
    }

    for (const segment of walls) {
      const row = tbody.querySelector(`[data-wall-id="${cssEscape(segment.id)}"]`);
      if (!row) {
        render(walls, selectedId, displayLength, displayAngle, displayThickness, thickPlaceholder, autoLength);
        return;
      }
      row.classList.toggle('wall-table__row--selected', segment.id === selectedId);

      const input = row.querySelector('.wall-table__input');
      if (input && input.ownerDocument.activeElement !== input) {
        input.value = displayLength(segment) ?? '';
        input.placeholder = autoPlaceholder(segment, autoLength);
        input.classList.toggle('wall-table__input--auto', isAutoLength(segment));
        input.classList.remove('wall-table__input--invalid');
      }
      const inherited = row.querySelector('.wall-table__measurement-state');
      if (inherited) inherited.hidden = segment.measurementState !== 'inherited';

      const lock = row.querySelector('.wall-table__snap-lock');
      if (lock) {
        const locked = segment.snapLengthLocked === true;
        lock.classList.toggle('is-active', locked);
        lock.setAttribute('aria-pressed', String(locked));
        lock.setAttribute('aria-label', `${locked ? 'Unlock' : 'Lock'} wall ${segment.wallNumber} length for snap close`);
        lock.title = locked ? 'Locked: snap close will not adjust this wall' : 'Unlocked: snap close may adjust this wall';
        lock.textContent = locked ? '🔒' : '🔓';
      }

      // The angle can change when an edit re-solves the chain, so refresh it —
      // but never while the picker is focused (mid-selection).
      const angleSelect = row.querySelector('.wall-table__angle-select');
      if (angleSelect && angleSelect.ownerDocument.activeElement !== angleSelect) {
        angleSelect.value = String(segment.angleDeg ?? 0);
      }

      // Refresh the thickness override too, but never while it is focused.
      const thick = row.querySelector('.wall-table__thick-input');
      if (thick && thick.ownerDocument.activeElement !== thick) {
        thick.value = displayThickness?.(segment) ?? '';
        thick.placeholder = thickPlaceholder ?? '';
        thick.classList.remove('wall-table__input--invalid');
      }

      const box = row.querySelector('.wall-table__elev-box');
      if (box) {
        box.checked = handlers.isPartition?.(segment.id)
          ? handlers.partitionElevation?.(segment.id) !== false
          : segment.includeInElevation !== false;
      }
    }
  }

  return { render, sync };
}

/**
 * Placeholder for a length input. A measured wall shows nothing (its value is
 * in the field). An unmeasured wall shows the solver-derived length so the
 * designer can see what "auto" resolved to instead of guessing — falling back
 * to the bare word "auto" before the room has been solved.
 */
function autoPlaceholder(segment, autoLength) {
  if (segment.isMeasured) return '';
  const solved = autoLength?.(segment);
  // Number first: a narrow length column may clip the placeholder, and the
  // solved length is the part the designer actually wants to read.
  return solved ? `${solved} auto` : 'auto';
}

/** True for an unmeasured real wall — the one case that shows an "auto" length. */
function isAutoLength(segment) {
  if (segment.isMeasured) return false;
  return (segment.segmentKind ?? 'wall') === 'wall';
}

/** CSS.escape when available (browser), a safe fallback otherwise (tests). */
function cssEscape(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return String(value).replace(/["\\]/g, '\\$&');
}
